import React, { useCallback, useEffect, useRef, useState } from "react";
import { Camera, Loader2, CheckCircle2, XCircle, Clock, Upload } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

/**
 * Price — Widget de gestion de la photo de profil.
 *
 * Le user upload une photo → statut "pending" → admin approuve ou refuse.
 * Une photo approuvée est visible sur les cartes chat / course.
 *
 * On compresse côté frontend avant l'envoi (canvas → webp 0.75 qualité,
 * max 400×400) pour rester sous ~150 KB de payload — le backend a une
 * limite de 800 000 chars en base64 (~600 KB binaire).
 */
export default function ProfilePhotoManager() {
  const [state, setState] = useState({ status: "loading", data_url: null, rejection_reason: null });
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);

  const refresh = useCallback(async () => {
    try {
      const r = await api.get("/price/me/profile-photo");
      setState({
        status: r.data?.status || "none",
        data_url: r.data?.data_url || null,
        rejection_reason: r.data?.rejection_reason || null,
      });
    } catch {
      setState({ status: "none", data_url: null, rejection_reason: null });
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const compressImage = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        // Cadre carré recentré, downscale à 400×400.
        const target = 400;
        const canvas = document.createElement("canvas");
        canvas.width = target;
        canvas.height = target;
        const ctx = canvas.getContext("2d");
        const side = Math.min(img.width, img.height);
        const sx = (img.width - side) / 2;
        const sy = (img.height - side) / 2;
        ctx.drawImage(img, sx, sy, side, side, 0, 0, target, target);
        // WebP si supporté, sinon JPEG.
        const dataUrl = canvas.toDataURL("image/webp", 0.75);
        resolve(dataUrl);
      };
      img.onerror = reject;
      img.src = reader.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Choisis un fichier image (JPG, PNG, WebP)");
      return;
    }
    setUploading(true);
    try {
      const data_url = await compressImage(file);
      if (data_url.length > 800_000) {
        toast.error("Photo trop lourde même après compression — réessaie avec une autre");
        return;
      }
      await api.post("/price/me/profile-photo", { data_url });
      toast.success("Photo envoyée — en attente de validation");
      await refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur d'envoi");
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const { status, data_url, rejection_reason } = state;

  const badge = {
    pending: { label: "En attente de validation", cls: "bg-[#FFF3CC] border-black", icon: Clock },
    approved: { label: "Photo validée", cls: "bg-[#D6F5D6] border-black", icon: CheckCircle2 },
    rejected: { label: "Photo refusée", cls: "bg-[#FFE5E5] border-black", icon: XCircle },
    none: { label: "Aucune photo", cls: "bg-white border-black", icon: Camera },
    loading: { label: "Chargement…", cls: "bg-white border-black", icon: Loader2 },
  }[status] || { label: "—", cls: "bg-white border-black", icon: Camera };

  const B = badge.icon;

  return (
    <div
      data-testid="profile-photo-manager"
      className="rounded-2xl bg-white p-4 border-2 border-black shadow-[0_4px_0_0_#000]"
    >
      <div className="flex items-center gap-3">
        <div className="relative w-20 h-20 rounded-2xl border-2 border-black overflow-hidden bg-[#FFF8F1] grid place-items-center shadow-[0_3px_0_0_#000] flex-none">
          {data_url ? (
            <img src={data_url} alt="Ma photo" className="w-full h-full object-cover" />
          ) : (
            <Camera className="w-8 h-8 text-neutral-400" strokeWidth={2} />
          )}
          {status === "approved" && (
            <div className="absolute -bottom-1 -right-1 w-6 h-6 rounded-full bg-[#2A9D3F] border-2 border-black grid place-items-center">
              <CheckCircle2 className="w-3.5 h-3.5 text-white" strokeWidth={3} />
            </div>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-[Outfit] text-[14px] font-black leading-none">Ma photo de profil</div>
          <div className={
            "mt-2 inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full border text-[10px] font-[Outfit] font-black uppercase tracking-widest " +
            badge.cls
          }>
            <B className={"w-3 h-3 " + (status === "loading" ? "animate-spin" : "")} strokeWidth={3} />
            {badge.label}
          </div>
          {status === "rejected" && rejection_reason && (
            <div className="mt-1 text-[11px] font-medium text-neutral-700">
              Motif : {rejection_reason}
            </div>
          )}
        </div>
      </div>
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        disabled={uploading}
        data-testid="profile-photo-upload"
        className="mt-3 w-full h-11 rounded-xl bg-[#FF3B3B] text-white font-[Outfit] font-black text-[13px] border-2 border-black shadow-[0_3px_0_0_#000] active:translate-y-[2px] active:shadow-[0_1px_0_0_#000] transition-all disabled:opacity-60 disabled:active:translate-y-0 flex items-center justify-center gap-2"
      >
        {uploading ? (
          <Loader2 className="w-4 h-4 animate-spin" />
        ) : (
          <>
            <Upload className="w-4 h-4" strokeWidth={3} />
            {status === "none" ? "Ajouter ma photo" : "Changer ma photo"}
          </>
        )}
      </button>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={handleFile}
        data-testid="profile-photo-input"
      />
      <p className="mt-2 text-[10px] font-medium text-neutral-500 leading-snug">
        L'admin Price valide chaque photo avant qu'elle apparaisse sur ton profil.
      </p>
    </div>
  );
}
